import { Box, Card, Flex, Text } from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";
import { Sparkline } from "~/v3-poc/components/Sparkline";
import {
  directionColor,
  directionOf,
  directionStroke,
  formatDecimal,
  formatPercent,
} from "../overview.format";
import type {
  IndexRowData,
  MarketIndicesCardPresentationProps,
} from "../types";

const NO_DATA = "No data";

export const MarketIndicesCardPresentation = ({
  rows,
  isLoading,
}: MarketIndicesCardPresentationProps) => (
  <Card size="2" className="flex-1">
    <Flex direction="column" gap="2" height="100%">
      <Flex direction="column" gap="1">
        <Text size="4" weight="bold">
          Market indices
        </Text>
        <Text size="1" color="gray">
          US benchmarks
        </Text>
      </Flex>

      <Flex direction="column" className="-mx-3">
        {rows.map((row, index) => (
          <IndexRow
            key={row.symbol}
            row={row}
            isLoading={isLoading}
            last={index === rows.length - 1}
          />
        ))}
      </Flex>
    </Flex>
  </Card>
);

const IndexRow = ({
  row,
  isLoading,
  last,
}: {
  row: IndexRowData;
  isLoading: boolean;
  last: boolean;
}) => {
  const hasFigures =
    !row.hasError && row.price !== undefined && row.previousClose !== undefined;
  const changeAbs = hasFigures
    ? (row.price as number) - (row.previousClose as number)
    : 0;
  const changePct =
    hasFigures && (row.previousClose as number) !== 0
      ? (changeAbs / (row.previousClose as number)) * 100
      : 0;
  const direction = directionOf(changeAbs);
  const showSpark = !row.hasSparkError && row.spark.length >= 2;

  return (
    <Flex
      align="center"
      justify="between"
      gap="2"
      px="3"
      py="2"
      className={cn(!last && "border-[color:var(--gray-a4)] border-b")}
    >
      <Box minWidth="0">
        <Text size="2" weight="bold" truncate className="block">
          {row.symbol}
        </Text>
        <Text size="1" color="gray" truncate className="block max-w-[130px]">
          {row.name}
        </Text>
      </Box>

      {showSpark ? (
        <Sparkline
          data={row.spark}
          width={64}
          height={22}
          // Match the % label (vs previous close), not spark first→last.
          color={hasFigures ? directionStroke(direction) : undefined}
        />
      ) : (
        <Text size="1" color="gray" className="w-16 shrink-0 text-center">
          {row.hasSparkError ? NO_DATA : "…"}
        </Text>
      )}

      <Flex direction="column" align="end" flexShrink="0">
        {hasFigures ? (
          <>
            <Text size="2" weight="medium" className="atd-numeric">
              {formatDecimal(row.price as number)}
            </Text>
            <Text
              size="1"
              weight="medium"
              color={directionColor(direction)}
              className="atd-numeric"
            >
              {formatPercent(changePct)}
            </Text>
          </>
        ) : (
          <Text size="1" color="gray" className="atd-numeric">
            {row.hasError || !isLoading ? NO_DATA : "…"}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};
